// src/components/AdLanding.jsx
import React, { useEffect, useRef, useState } from 'react';
import { collection, getCountFromServer, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import CountUp from 'react-countup';
import { Link } from 'react-router-dom';
import chromeLogo from '../assets/chrome-logo.png';
import shopifyLogo from '../assets/shopify-logo.png';
import claudeLogo from '../assets/claude-logo.png';
import xeroLogo from '../assets/xero-logo.png';
import klaviyoLogo from '../assets/klaviyo-logo.png';
import shepherdHero from '../assets/shepherdHeroVid.mp4';
import step1 from '../assets/step1.png';
import step2 from '../assets/step2.png';
import step3 from '../assets/step3.png';

const INSTALL_URL = 'https://chromewebstore.google.com/detail/shepherd-build-or-follow/okjbbpkkanbhfpmmafpmijcekpjjcncl';

const benefits = [
  {
    title: 'Knows the page you\'re on',
    desc: 'No setup, no configuration. Shepherd reads the app you\'re in and shows guidance for exactly that screen.',
    icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 3L3 7v5c0 4.418 3.358 8.547 8 9.6C16.642 20.547 20 16.418 20 12V7L11 3z" stroke="#1a73e8" strokeWidth="1.6" strokeLinejoin="round"/></svg>,
  },
  {
    title: 'Built by people, not manuals',
    desc: 'Every guide comes from someone who already solved the exact problem you\'re stuck on.',
    icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="8" r="4" stroke="#1a73e8" strokeWidth="1.6"/><path d="M5 19c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="#1a73e8" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  },
  {
    title: 'Works on almost anything',
    desc: 'Shopify, Notion, Xero, Salesforce, Linear — if it runs in your browser, Shepherd can guide you through it.',
    icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 2L2 7l9 5 9-5-9-5zM2 17l9 5 9-5M2 12l9 5 9-5" stroke="#1a73e8" strokeWidth="1.6" strokeLinejoin="round"/></svg>,
  },
  {
    title: 'Stuck? Just ask',
    desc: 'Mid-guide questions get answered in context — no tab-switching to a help article that\'s three versions out of date.',
    icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 2v4M11 16v4M4.22 4.22l2.83 2.83M14.95 14.95l2.83 2.83M2 11h4M16 11h4M4.22 17.78l2.83-2.83M14.95 7.05l2.83-2.83" stroke="#1a73e8" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  },
];

const steps = [
  { num: '1', title: 'Install the extension', desc: 'One click, no account required to get started. Shepherd sits quietly in your toolbar.', image: step1 },
  { num: '2', title: 'Open any supported app', desc: 'Shepherd recognises the software you\'re using and surfaces relevant guides automatically.', image: step2 },
  { num: '3', title: 'Follow the highlighted steps', desc: 'Click through a guide built by someone who\'s done this exact task before. No guesswork.', image: step3 },
];

const testimonials = [
  { text: "I onboarded onto Linear in 20 minutes instead of two days. The Shepherd guide was clearer than anything in their docs.", name: 'Sam R.', role: 'Founder, Northbrook Labs', initials: 'SR' },
  { text: "I built a guide for our internal CRM and shared it with the team. They stopped asking me the same questions. Game changer.", name: 'Jenna K.', role: 'Ops Lead, Meridian', initials: 'JK' },
  { text: "My mum uses it to navigate her accounting software. She hasn't called me for help in three weeks. Honestly emotional.", name: 'Tom M.', role: 'Developer, Cardiff', initials: 'TM' },
];

const faqs = [
  { q: 'Is Shepherd actually free?', a: 'Yes. Installing Shepherd and following community guides is free. Some advanced creator features are part of an optional paid plan, but you never need it just to get guided through software.' },
  { q: 'Will it slow down my browser?', a: 'No. Shepherd only activates on pages where it has relevant guidance, and stays completely inactive everywhere else.' },
  { q: 'What does Shepherd see on my screen?', a: 'Shepherd only reads the interface elements needed to show you where to click — buttons, labels, menus, form fields. It doesn\'t read your private data, and passwords or payment fields are never collected.' },
  { q: 'Which websites does it work on?', a: 'Shepherd works across a growing library of business software including Shopify, Notion, Xero, Klaviyo, Salesforce and more — and community members are adding new guides constantly.' },
  { q: 'Do I need to create an account?', a: 'No account is needed to install Shepherd and start following guides. An account is only needed if you want to save progress or create your own guides.' },
];

function useIsMobile(breakpoint = 860) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

function Reveal({ children, style, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) e.target.classList.add('ad-visible'); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className={`ad-reveal ${className}`} style={style}>{children}</div>;
}

function InstallButton({ style, children = "Install for Chrome — It's free" }) {
  return (
    <a href={INSTALL_URL} target="_blank" rel="noreferrer" style={{ ...styles.btnPrimary, ...style }}>
      <img src={chromeLogo} alt="" style={{ width: 24, height: 24 }} />
      {children}
    </a>
  );
}

export default function AdLanding() {
  const isMobile = useIsMobile();
  const [users, setUsers] = useState(null);
  const [guides, setGuides] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    async function fetchStats() {
      try {
        const userCountSnap = await getCountFromServer(collection(db, 'users'));
        const playbookSnap = await getDocs(collection(db, 'playbooks'));
        setUsers(userCountSnap.data().count);
        setGuides(playbookSnap.size);
      } catch (e) {
        console.warn('Error:', e.message);
      }
    }
    fetchStats();
  }, []);

  return (
    <div style={styles.page}>
      <style>{`
        .ad-reveal { opacity:0; transform:translateY(20px); transition: opacity 0.6s ease, transform 0.6s ease; }
        .ad-reveal.ad-visible { opacity:1; transform:none; }
        .ad-faq-item:hover { border-color: var(--brand-light); }
      `}</style>

      {/* Minimal header: no nav links out, just brand + CTA */}
      <header style={styles.header}>
        <div style={styles.logoRow}>
          <span style={styles.logo}>Shepherd</span>
          <span style={styles.betaBadge}>BETA</span>
        </div>
        {!isMobile && <InstallButton style={styles.headerBtn}>Install for Chrome</InstallButton>}
      </header>

      {/* HERO */}
      <section style={{ ...styles.hero, padding: isMobile ? '2.5rem 1.5rem 3rem' : '3.5rem clamp(1.5rem, 6vw, 5rem) 4rem' }}>
        <div style={{ ...styles.heroInner, gridTemplateColumns: isMobile ? '1fr' : '1.05fr 1fr', textAlign: isMobile ? 'center' : 'left', gap: isMobile ? '2rem' : '4rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}>
            <span style={styles.eyebrow}>Chrome Extension &middot; Free to install</span>
            <h1 style={styles.h1}>Stop Googling "how do I&hellip;" for every new tool</h1>
            <p style={styles.sub}>
              Shepherd guides you step-by-step, right inside the software you're already using — built by real users who've already figured it out.
            </p>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', width: isMobile ? '100%' : 'auto' }}>
              <InstallButton style={{ width: isMobile ? '100%' : 'auto', justifyContent: 'center' }} />
            </div>
            <div style={styles.trustRow}>
              <span style={styles.trustItem}><CheckIcon /> No credit card</span>
              <span style={styles.trustItem}><CheckIcon /> 60-second install</span>
              <span style={styles.trustItem}><CheckIcon /> Works instantly</span>
            </div>
          </div>

          <div style={styles.browserMock}>
            <div style={styles.bar}>
              <div style={styles.dots}>
                <div style={{ ...styles.dot, background: '#fc5454' }} />
                <div style={{ ...styles.dot, background: '#ffbe2e' }} />
                <div style={{ ...styles.dot, background: '#2dc254' }} />
              </div>
              <div style={styles.url}>admin.shopify.com/store</div>
            </div>
            <div style={styles.videoContainer}>
              <video autoPlay muted loop playsInline style={styles.video}>
                <source src={shepherdHero} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF STRIP */}
      <section style={styles.proofSection}>
        <div style={{ ...styles.proof, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr' }}>
          <div style={{ ...styles.stat, borderRight: isMobile ? 'none' : '1px solid var(--border)', borderBottom: isMobile ? '1px solid var(--border)' : 'none' }}>
            <div style={styles.statNum}>{users !== null ? <CountUp end={users} duration={2} separator="," /> : <span>&mdash;</span>}</div>
            <div style={styles.statLabel}>Early users</div>
          </div>
          <div style={{ ...styles.stat, borderRight: isMobile ? 'none' : '1px solid var(--border)', borderBottom: isMobile ? '1px solid var(--border)' : 'none' }}>
            <div style={styles.statNum}>{guides !== null ? <>+<CountUp end={guides} duration={2.5} /></> : <span>&mdash;</span>}</div>
            <div style={styles.statLabel}>Guides shared</div>
          </div>
          <div style={styles.stat}>
            <div style={{ ...styles.statNum, fontSize: 'clamp(1.1rem, 2.5vw, 1.6rem)' }}>4.9/5</div>
            <div style={styles.statLabel}>Average rating</div>
          </div>
        </div>
        <div style={{ ...styles.logoStrip, flexWrap: 'wrap' }}>
          <span style={styles.logoStripLabel}>Works with</span>
          <img src={shopifyLogo} alt="Shopify" style={styles.brandLogo} />
          <img src={klaviyoLogo} alt="Klaviyo" style={styles.brandLogo} />
          <img src={claudeLogo} alt="Claude" style={styles.brandLogo} />
          <img src={xeroLogo} alt="Xero" style={styles.brandLogo} />
        </div>
      </section>

      {/* BENEFITS */}
      <Reveal style={styles.section}>
        <div style={styles.inner}>
          <span style={styles.label}>Why Shepherd</span>
          <h2 style={styles.h2}>Software shouldn't need a manual</h2>
          <p style={styles.sectionSub}>No documentation hunting. No 10-minute tutorial videos. Just clear guidance where you need it.</p>
          <div style={styles.grid}>
            {benefits.map((f) => (
              <div key={f.title} style={styles.card}>
                <div style={styles.iconBox}>{f.icon}</div>
                <h3 style={styles.cardTitle}>{f.title}</h3>
                <p style={styles.cardDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* HOW IT WORKS */}
      <Reveal style={{ ...styles.section, background: 'var(--warm-cream)' }}>
        <div style={styles.inner}>
          <span style={styles.label}>How it works</span>
          <h2 style={styles.h2}>Up and running in under a minute</h2>
          <div style={styles.stepsGrid}>
            {steps.map((step) => (
              <div key={step.num} style={styles.stepCard}>
                <div style={styles.stepNum}>{step.num}</div>
                <h3 style={styles.cardTitle}>{step.title}</h3>
                <p style={styles.cardDesc}>{step.desc}</p>
                <div style={styles.imgContainer}>
                  <img src={step.image} alt={step.title} style={styles.screenshot} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <InstallButton />
          </div>
        </div>
      </Reveal>

      {/* TESTIMONIALS */}
      <Reveal style={styles.section}>
        <div style={styles.inner}>
          <span style={styles.label}>What people say</span>
          <h2 style={styles.h2}>Trusted because it's built by people like you</h2>
          <div style={{ ...styles.testimonialGrid, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr' }}>
            {testimonials.map((t) => (
              <div key={t.name} style={styles.testimonialCard}>
                <p style={styles.quote}>&ldquo;{t.text}&rdquo;</p>
                <div style={styles.author}>
                  <div style={styles.avatar}>{t.initials}</div>
                  <div>
                    <div style={styles.name}>{t.name}</div>
                    <div style={styles.role}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* FAQ */}
      <Reveal style={{ ...styles.section, background: 'var(--warm-cream)' }}>
        <div style={{ ...styles.inner, maxWidth: 720 }}>
          <span style={styles.label}>Common questions</span>
          <h2 style={styles.h2}>Before you install</h2>
          <div style={{ marginTop: '2rem' }}>
            {faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} className="ad-faq-item" style={styles.faqItem}>
                  <button style={styles.faqQuestion} onClick={() => setOpenFaq(open ? -1 : i)}>
                    {f.q}
                    <span style={{ ...styles.faqChevron, transform: open ? 'rotate(180deg)' : 'none' }}>&#9660;</span>
                  </button>
                  {open && <p style={styles.faqAnswer}>{f.a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* FINAL CTA */}
      <section style={styles.finalCta}>
        <div style={styles.finalInner}>
          <h2 style={styles.finalH2}>Make software easy to use</h2>
          <p style={styles.finalSub}>Add Shepherd to Chrome in one click and never dig through a help centre again.</p>
          <InstallButton style={{ margin: '0 auto' }} />
          <p style={styles.finalNote}>Free to install &middot; No credit card &middot; Beta V1</p>
        </div>
      </section>

      {/* MINIMAL FOOTER */}
      <footer style={styles.footer}>
        <span style={styles.footerCopy}>&copy; 2026 Shepherd &mdash; A Luke Mancuso Product</span>
        <div style={styles.footerLinks}>
          <Link to="/legal" style={styles.footerLink}>Legal Hub</Link>
          <a href="mailto:luke@startshepherd.com" style={styles.footerLink}>Contact</a>
        </div>
      </footer>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, flexShrink: 0 }}>
      <circle cx="7" cy="7" r="7" fill="var(--brand)" />
      <path d="M4 7l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const styles = {
  page: { background: 'var(--warm-white)', overflowX: 'hidden' },

  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 clamp(1.5rem, 5vw, 4rem)', height: 64,
    borderBottom: '1px solid var(--border)', background: 'rgba(250,251,255,0.9)',
    position: 'sticky', top: 0, zIndex: 100, backdropFilter: 'blur(8px)',
  },
  logoRow: { display: 'flex', alignItems: 'center', gap: 12 },
  logo: { fontFamily: 'magnolia-script', color: 'var(--brand)', fontSize: '1.6rem' },
  betaBadge: { fontSize: 10, fontWeight: 700, color: '#666', background: '#f1f3f4', border: '1px solid #e0e0e0', padding: '3px 7px', borderRadius: 6, letterSpacing: '0.05em', textTransform: 'uppercase' },
  headerBtn: { padding: '9px 18px', fontSize: '0.9rem' },

  hero: {
    background: 'radial-gradient(ellipse 60% 70% at 70% 30%, rgba(26,115,232,0.08) 0%, transparent 70%), var(--warm-white)',
  },
  heroInner: { maxWidth: 1180, margin: '0 auto', display: 'grid', alignItems: 'center' },
  eyebrow: { display: 'inline-block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1rem' },
  h1: { fontFamily: "'Sora', sans-serif", fontSize: 'clamp(2.1rem, 4.4vw, 3.4rem)', fontWeight: 700, lineHeight: 1.12, color: 'var(--charcoal)', marginBottom: '1.1rem' },
  sub: { fontSize: 'clamp(1rem, 1.6vw, 1.15rem)', color: 'var(--text-muted)', maxWidth: 480, marginBottom: '1.75rem', fontWeight: 300, lineHeight: 1.65 },
  trustRow: { display: 'flex', flexWrap: 'wrap', gap: '1.25rem', marginTop: '1.5rem' },
  trustItem: { display: 'flex', alignItems: 'center', fontSize: '0.85rem', color: 'var(--text-mid)', fontWeight: 500 },

  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'var(--brand)', color: 'white',
    padding: '15px 28px', borderRadius: 'var(--radius-md)',
    fontSize: '1.05rem', fontWeight: 600,
    textDecoration: 'none', cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(26,115,232,0.28)',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },

  browserMock: {
    width: '100%', background: 'white', borderRadius: 12, border: '1px solid var(--border)',
    overflow: 'hidden', boxShadow: '0 32px 80px rgba(26,31,54,0.12), 0 4px 16px rgba(26,31,54,0.06)',
  },
  bar: { background: '#f1f3f4', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #ddd' },
  dots: { display: 'flex', gap: 6 },
  dot: { width: 10, height: 10, borderRadius: '50%' },
  url: { flex: 1, background: 'white', borderRadius: 6, padding: '4px 10px', fontSize: '0.75rem', color: 'var(--text-muted)', border: '1px solid #ddd', textAlign: 'center' },
  videoContainer: { width: '100%', aspectRatio: '16 / 9', lineHeight: 0, background: '#000', overflow: 'hidden' },
  video: { width: '100%', height: '100%', display: 'block', objectFit: 'cover' },

  proofSection: { background: 'var(--warm-cream)', padding: '2rem 1.5rem 3rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem' },
  proof: { display: 'grid', width: '100%', maxWidth: 900, background: 'white', borderRadius: 'var(--radius-lg)', boxShadow: '0 20px 50px rgba(26,31,54,0.08)', overflow: 'hidden' },
  stat: { textAlign: 'center', padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  statNum: { fontFamily: "'Sora', sans-serif", fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 800, color: 'var(--charcoal)', lineHeight: 1 },
  statLabel: { fontSize: '0.72rem', color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 },
  logoStrip: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2rem' },
  logoStripLabel: { fontSize: '0.78rem', color: 'var(--text-light)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' },
  brandLogo: { height: 22, opacity: 0.7, objectFit: 'contain' },

  section: { padding: 'clamp(3.5rem, 7vw, 6rem) clamp(1.5rem, 6vw, 5rem)' },
  inner: { maxWidth: 1080, margin: '0 auto', textAlign: 'center' },
  label: { display: 'inline-block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.8rem' },
  h2: { fontFamily: "'Sora', sans-serif", fontSize: 'clamp(1.7rem, 3.6vw, 2.5rem)', fontWeight: 700, lineHeight: 1.15, color: 'var(--charcoal)', marginBottom: '0.75rem' },
  sectionSub: { fontSize: '1.02rem', color: 'var(--text-muted)', maxWidth: 520, margin: '0 auto', fontWeight: 300 },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '2.75rem', textAlign: 'left' },
  card: { padding: '1.8rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', background: 'white' },
  iconBox: { width: 44, height: 44, borderRadius: 11, background: 'var(--brand-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' },
  cardTitle: { fontFamily: "'Sora', sans-serif", fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--charcoal)' },
  cardDesc: { fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.65 },

  stepsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.75rem', marginTop: '2.75rem', textAlign: 'left' },
  stepCard: { background: 'white', borderRadius: 20, padding: '1.75rem', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column' },
  stepNum: { fontFamily: "'Sora', sans-serif", fontSize: '1.3rem', fontWeight: 700, color: 'var(--brand-pale)', marginBottom: '0.75rem' },
  imgContainer: { marginTop: '1.25rem', background: '#f8f9ff', borderRadius: 14, padding: 8, border: '1px solid #f0f2f8', overflow: 'hidden' },
  screenshot: { width: '100%', height: 'auto', borderRadius: 8, display: 'block', objectFit: 'cover' },

  testimonialGrid: { display: 'grid', gap: '1.25rem', marginTop: '2.75rem', textAlign: 'left' },
  testimonialCard: { background: 'white', borderRadius: 'var(--radius-md)', padding: '1.5rem', border: '1px solid var(--border)' },
  quote: { fontSize: '0.92rem', color: 'var(--text-mid)', lineHeight: 1.65, marginBottom: '1.1rem', fontStyle: 'italic' },
  author: { display: 'flex', alignItems: 'center', gap: 10 },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: 'var(--brand-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 600, color: 'var(--brand)', flexShrink: 0 },
  name: { fontSize: '0.85rem', fontWeight: 600, color: 'var(--charcoal)' },
  role: { fontSize: '0.76rem', color: 'var(--text-light)' },

  faqItem: { borderBottom: '1px solid var(--border)', textAlign: 'left', padding: '0.4rem 0' },
  faqQuestion: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: '1rem 0.25rem', fontSize: '1rem', fontWeight: 600, color: 'var(--charcoal)', fontFamily: 'inherit', textAlign: 'left' },
  faqChevron: { fontSize: '0.7rem', color: 'var(--brand)', transition: 'transform 0.2s', marginLeft: 12, flexShrink: 0 },
  faqAnswer: { fontSize: '0.92rem', color: 'var(--text-muted)', lineHeight: 1.65, padding: '0 0.25rem 1.1rem', margin: 0 },

  finalCta: { background: 'var(--charcoal)', padding: 'clamp(3.5rem, 7vw, 6rem) clamp(1.5rem, 6vw, 5rem)' },
  finalInner: { maxWidth: 620, margin: '0 auto', textAlign: 'center' },
  finalH2: { fontFamily: "'Sora', sans-serif", fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 700, lineHeight: 1.15, color: 'white', marginBottom: '0.9rem' },
  finalSub: { fontSize: '1.02rem', color: 'rgba(255,255,255,0.55)', maxWidth: 440, margin: '0 auto 2rem', fontWeight: 300 },
  finalNote: { fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginTop: '1.25rem' },

  footer: { display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between', padding: '2rem clamp(1.5rem, 6vw, 5rem)', background: '#121212' },
  footerCopy: { fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)' },
  footerLinks: { display: 'flex', gap: '1.5rem' },
  footerLink: { fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', textDecoration: 'none' },
};
